import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { GlassCard } from "@/components/mnf/UI";
import { useConvexAuth } from "convex/react";
import { hydrateFromServer, startAutoSave } from "@/lib/snapshotSync";
import { track } from "@/lib/telemetry";

/**
 * AuthVerify
 *
 * Legacy landing for any inbound auth callback (kept for safety).
 * The new password flow does verification inside SignIn.tsx, but if a stale
 * link or OAuth callback lands here we wait briefly for Convex to flip
 * isAuthenticated, then route home. If it doesn't flip, we send the user
 * to /signin to sign in with their password.
 */
export default function AuthVerify() {
  const [, setLocation] = useLocation();
  const { isLoading, isAuthenticated } = useConvexAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      track("auth_verify_ok");
      // Honour the HydrateOutcome contract (lib/snapshotSync.ts): auto-save MUST
      // NOT start on "failed". This discarded the outcome and always started, so
      // a hydrate that failed on a network blip let the 3s debounced save write
      // the SEEDED demo DB over her real server snapshot -- destroying every meal
      // log, prayer and workout since signup, while the only message she saw said
      // her data was untouched. It also raced AuthBoot: startAutoSave is
      // idempotent, so whichever ran first won and AuthBoot's guard became dead.
      const outcome = await hydrateFromServer();
      if (cancelled) return;
      if (outcome === "restored" || outcome === "empty") {
        startAutoSave();
      } else {
        console.error(
          "[AuthVerify] hydrate failed — auto-save disabled to protect existing server data",
        );
      }
      setTimeout(() => setLocation("/"), 300);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setLocation]);

  const showFail = timedOut && !isAuthenticated && !isLoading;

  return (
    <div style={{ minHeight: "100vh", padding: 22, background: "var(--mnf-cream)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <GlassCard padding={22}>
        {!showFail && !isAuthenticated && (
          <p className="mnf-serif" style={{ fontSize: 16, color: "var(--mnf-fd)", textAlign: "center" }}>
            Welcoming you in...
          </p>
        )}
        {isAuthenticated && (
          <p className="mnf-serif" style={{ fontSize: 16, color: "var(--mnf-fd)", textAlign: "center" }}>
            Signed in. Carrying you home.
          </p>
        )}
        {showFail && (
          <>
            <p className="mnf-serif" style={{ fontSize: 16, color: "var(--mnf-fd)", textAlign: "center" }}>
              That link no longer works.
            </p>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", textAlign: "center", marginTop: 8, lineHeight: 1.55 }}>
              We've moved to email and password. Please sign in with your password instead.
            </p>
            <div className="mt-4 flex justify-center">
              <button
                data-testid="button-back-to-signin"
                onClick={() => setLocation("/signin")}
                className="mnf-body hover-elevate"
                style={{
                  padding: "10px 18px", borderRadius: 999,
                  background: "var(--mnf-f)", color: "#F5F0E8",
                  border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}
              >
                Go to sign in
              </button>
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}
